import { render } from '../main';
import { qaApi } from '../api';
import { navigate } from '../router';
import {
  truncate,
  loadingSpinner,
  emptyState,
  errorState,
} from '../utils';
import { renderSortMenu, getSortLabel, setupSortMenuHandlers, type SortRow } from '../components/sort-menu';
import { renderListItem } from '../components/list-item';

type SortField = 'recent' | 'name' | 'questions';
type SortDirection = 'asc' | 'desc';

interface QaSetItem {
  id: string;
  title: string;
  description: string | null;
  avatar_url: string | null;
  question_count: number;
  created_at: string;
}

const SORT_ROWS: SortRow[][] = [
  [
    { key: 'recent-desc', icon: 'clock', label: 'Recent' },
    { key: 'recent-asc', icon: 'history', label: 'Oldest' },
  ],
  [
    { key: 'name-asc', icon: 'sort-alpha-down', label: 'Name A-Z' },
    { key: 'name-desc', icon: 'sort-alpha-up', label: 'Name Z-A' },
  ],
  [
    { key: 'questions-desc', icon: 'list-ol', label: 'Most Qs' },
    { key: 'questions-asc', icon: 'list', label: 'Least Qs' },
  ],
];

let currentSort: SortField = 'recent';
let currentDirection: SortDirection = 'desc';
let sets: QaSetItem[] = [];

function getCurrentSortKey(): string {
  return `${currentSort}-${currentDirection}`;
}

function getCurrentSortLabel(): string {
  return getSortLabel(SORT_ROWS, getCurrentSortKey());
}

export async function renderQuestions(): Promise<void> {
  const currentKey = getCurrentSortKey();

  render(`
    <div class="view-frame">
      <div class="browse-header" style="justify-content: flex-end;">
        <button class="browse-pill" id="sort-btn">
          <span class="browse-pill-main">Sort <i class="fas fa-sort"></i></span>
          <span class="browse-pill-label" id="sort-label">${getCurrentSortLabel()}</span>
        </button>
      </div>
      ${renderSortMenu(SORT_ROWS, currentKey)}
      <div class="view-scroll" id="questions-list">
        ${loadingSpinner()}
      </div>
    </div>
  `);

  setupSortHandlers();
  await loadSets();
}

function setupSortHandlers(): void {
  setupSortMenuHandlers(
    getCurrentSortKey,
    (newKey) => {
      const [field, direction] = newKey.split('-') as [SortField, SortDirection];
      currentSort = field;
      currentDirection = direction;
      loadSets();
      const list = document.getElementById('questions-list');
      if (list) list.scrollTop = 0;
    },
  );
}

async function loadSets(): Promise<void> {
  const container = document.getElementById('questions-list')!;

  try {
    const response = await qaApi.listSets({
      sort: currentSort,
      sort_dir: currentDirection,
    });
    sets = response.data.sets;

    if (sets.length === 0) {
      container.innerHTML = emptyState({
        icon: 'clipboard-question',
        title: 'No question packs yet',
        message: 'Check back soon for new question packs',
      });
      return;
    }

    renderList();
  } catch (err: any) {
    container.innerHTML = errorState({
      title: 'Failed to load question packs',
      retryId: 'retry-btn',
    });
    document.getElementById('retry-btn')?.addEventListener('click', loadSets);
  }
}

function renderList(): void {
  const container = document.getElementById('questions-list')!;

  container.innerHTML = `
    <div class="friends-list">
      ${sets.map(set => renderSetItem(set)).join('')}
    </div>
  `;

  sets.forEach(set => {
    const item = document.getElementById(`qa-set-${set.id}`);
    if (item) {
      item.addEventListener('click', () => navigate(`/questions/${set.id}`));
    }
  });
}

function renderSetItem(set: QaSetItem): string {
  const description = set.description
    ? truncate(set.description, 200)
    : 'No description';

  const qCount = set.question_count === 1 ? '1 question' : `${set.question_count} questions`;

  return renderListItem({
    id: `qa-set-${set.id}`,
    avatarUrl: set.avatar_url,
    avatarFallbackIcon: 'clipboard-question',
    title: set.title,
    metaHtml: `<span class="friend-info">${qCount}</span>`,
    preview: description,
  });
}
